import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { themeManager, Theme, AUTO_THEME_ID } from '../utils/themeManager';

interface ThemeSelectorProps {
  className?: string;
}

const SelectorContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 16px;
  color: white;
  min-width: 250px;
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  text-align: center;
  color: white;
`;

const ThemeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const ThemeOption = styled.button<{ $isActive: boolean; $preview: string }>`
  background: ${props => props.$preview};
  border: 3px solid ${props => props.$isActive ? 'white' : 'transparent'};
  border-radius: 8px;
  padding: 12px 8px;
  cursor: pointer;
  color: white;
  font-size: 12px;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: scale(1.05);
    border-color: rgba(255,255,255,0.7);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$preview};
    opacity: 0.8;
    z-index: -1;
  }
`;

const ThemePreview = styled.div<{ $theme: Theme }>`
  width: 100%;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.$theme.background};
  margin-bottom: 4px;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.$theme.accent};
    box-shadow: 
      10px 0 0 ${props => props.$theme.primary},
      20px 0 0 ${props => props.$theme.secondary};
  }
`;

const ThemeName = styled.div`
  font-size: 11px;
  margin-top: 4px;
`;

const PreviewModal = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const PreviewContainer = styled.div<{ $theme: Theme }>`
  background: ${props => props.$theme.background};
  border-radius: 16px;
  padding: 30px;
  max-width: 500px;
  width: 100%;
  color: ${props => props.$theme.text};
  text-align: center;
`;

const PreviewTitle = styled.h2<{ $theme: Theme }>`
  color: ${props => props.$theme.text};
  margin: 0 0 20px 0;
`;

const PreviewDice = styled.div<{ $theme: Theme }>`
  display: inline-block;
  width: 60px;
  height: 60px;
  background: ${props => props.$theme.diceGradient};
  border-radius: 12px;
  margin: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  color: ${props => props.$theme.diceNumbers};
  box-shadow: 
    0 4px 8px ${props => props.$theme.diceShade},
    inset 0 1px 0 rgba(255,255,255,0.2);
`;

const PreviewButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
`;

const PreviewButton = styled.button<{ $theme: Theme; $variant?: 'primary' | 'secondary' }>`
  background: ${props => props.$variant === 'secondary' ? props.$theme.secondary : props.$theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  }
`;

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ className }) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(themeManager.getCurrentTheme());
  // CHANGE (2026-08-15): Preferensen (tema-id eller 'auto') styr vilket kort
  // som markeras – i auto-läge ska Auto-kortet lysa, inte det resolvade temat.
  const [preference, setPreference] = useState<string>(themeManager.getPreference());
  const [previewTheme, setPreviewTheme] = useState<Theme | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const unsubscribe = themeManager.subscribe((theme: Theme) => {
      setCurrentTheme(theme);
      setPreference(themeManager.getPreference());
    });

    return unsubscribe;
  }, []);

  const handleThemeSelect = (theme: Theme) => {
    themeManager.setTheme(theme.id);
  };

  const handlePreview = (theme: Theme) => {
    setPreviewTheme(theme);
    setShowPreview(true);
  };

  const handleApplyPreview = () => {
    if (previewTheme) {
      themeManager.setTheme(previewTheme.id);
    }
    setShowPreview(false);
  };

  const themes = themeManager.getAllThemes();

  return (
    <>
      <SelectorContainer className={className}>
        <Title>🎨 Teman</Title>
        <ThemeGrid>
          <ThemeOption
            key="auto"
            $isActive={preference === AUTO_THEME_ID}
            $preview="linear-gradient(105deg, #f8f9fa 0%, #f8f9fa 49%, #000428 51%, #004e92 100%)"
            onClick={() => themeManager.setTheme(AUTO_THEME_ID)}
            title="Följer systemets ljus/mörk-inställning"
          >
            <ThemePreview $theme={{
              ...currentTheme,
              background: 'linear-gradient(105deg, #f8f9fa 0%, #f8f9fa 49%, #000428 51%, #004e92 100%)',
            }} />
            <ThemeName>🌗 Auto (system)</ThemeName>
          </ThemeOption>
          {themes.map(theme => (
            <ThemeOption
              key={theme.id}
              $isActive={preference !== AUTO_THEME_ID && currentTheme.id === theme.id}
              $preview={theme.background}
              onClick={() => handleThemeSelect(theme)}
              onDoubleClick={() => handlePreview(theme)}
            >
              <ThemePreview $theme={theme} />
              <ThemeName>{theme.name}</ThemeName>
            </ThemeOption>
          ))}
        </ThemeGrid>
        
        <div style={{ fontSize: '11px', marginTop: '12px', opacity: '0.7', textAlign: 'center' }}>
          Dubbelklicka för förhandsvisning
        </div>
      </SelectorContainer>

      <PreviewModal $isVisible={showPreview}>
        {previewTheme && (
          <PreviewContainer $theme={previewTheme}>
            <PreviewTitle $theme={previewTheme}>
              {previewTheme.name}
            </PreviewTitle>
            
            <div style={{ marginBottom: '20px' }}>
              <PreviewDice $theme={previewTheme}>1</PreviewDice>
              <PreviewDice $theme={previewTheme}>2</PreviewDice>
              <PreviewDice $theme={previewTheme}>3</PreviewDice>
            </div>
            
            <div style={{ fontSize: '14px', opacity: '0.8', marginBottom: '20px' }}>
              Så här kommer spelet att se ut med detta tema
            </div>
            
            <PreviewButtons>
              <PreviewButton 
                $theme={previewTheme} 
                $variant="primary"
                onClick={handleApplyPreview}
              >
                Använd detta tema
              </PreviewButton>
              <PreviewButton 
                $theme={previewTheme} 
                $variant="secondary"
                onClick={() => setShowPreview(false)}
              >
                Avbryt
              </PreviewButton>
            </PreviewButtons>
          </PreviewContainer>
        )}
      </PreviewModal>
    </>
  );
};

export default ThemeSelector;
